import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Inbox, CheckCircle2, RefreshCw, AlertCircle, FileCode, Download, FileText, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

function parseUbl(xml) {
  try {
    const doc = new DOMParser().parseFromString(xml || "", "application/xml");
    if (!xml || doc.getElementsByTagName("parsererror").length) return null;
    const first = (sel) => doc.getElementsByTagName(sel)[0]?.textContent?.trim() || "";
    const party = (tag) => {
      const el = doc.getElementsByTagName(tag)[0];
      if (!el) return "";
      return (el.getElementsByTagName("cbc:RegistrationName")[0]?.textContent
        || el.getElementsByTagName("cbc:Name")[0]?.textContent || "").trim();
    };
    const lines = Array.from(doc.getElementsByTagName("cac:InvoiceLine")).map((l) => ({
      name: l.getElementsByTagName("cbc:Name")[0]?.textContent?.trim() || "",
      qty: l.getElementsByTagName("cbc:InvoicedQuantity")[0]?.textContent?.trim() || "",
      amount: l.getElementsByTagName("cbc:LineExtensionAmount")[0]?.textContent?.trim() || "",
    }));
    return {
      number: first("cbc:ID"),
      issue: first("cbc:IssueDate"),
      due: first("cbc:DueDate"),
      currency: first("cbc:DocumentCurrencyCode") || "EUR",
      supplier: party("cac:AccountingSupplierParty"),
      customer: party("cac:AccountingCustomerParty"),
      payable: first("cbc:PayableAmount"),
      tax: doc.getElementsByTagName("cbc:TaxAmount")[0]?.textContent?.trim() || "",
      lines,
    };
  } catch { return null; }
}

export default function PrijateEFaktury() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [acking, setAcking] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [downloading, setDownloading] = useState("");

  const downloadXml = () => {
    const xml = detail?.payload;
    if (!xml) { toast.error("XML nie je dostupné."); return; }
    const sid = detail?.message_id || detail?.id || "faktura";
    const blob = new Blob([xml], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `prijata_efaktura_${sid}.xml`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async () => {
    const sid = detail?.message_id || detail?.id;
    if (!sid) return;
    setDownloading("pdf");
    try {
      const { data } = await api.get(`/efaktura/received/${encodeURIComponent(sid)}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `prijata_efaktura_${sid}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = "Stiahnutie PDF zlyhalo.";
      try {
        const txt = await e.response?.data?.text?.();
        if (txt) msg = formatApiErrorDetail(JSON.parse(txt)?.detail) || msg;
      } catch { /* keep default */ }
      toast.error(msg);
    } finally { setDownloading(""); }
  };

  const openDetail = async (d) => {
    const id = d.id || d.message_id;
    setDetail({ ...d, payload: "" }); setDetailLoading(true); setShowRaw(false);
    try { const { data } = await api.get(`/efaktura/received/${encodeURIComponent(id)}/detail`); setDetail((x) => ({ ...x, ...data })); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try { const { data } = await api.get("/efaktura/received"); setDocs(Array.isArray(data) ? data : []); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || "Nepodarilo sa načítať prijaté e-faktúry"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (user?.role === "admin") load(); }, [load, user]);

  const acknowledge = async (d) => {
    const id = d.id || d.message_id;
    setAcking(id);
    try {
      await api.post(`/efaktura/received/${encodeURIComponent(id)}/acknowledge`);
      toast.success("Prevzatie potvrdené");
      setDocs((xs) => xs.map((x) => ((x.id || x.message_id) === id ? { ...x, acknowledged: true } : x)));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAcking(""); }
  };

  if (!isAdmin) {
    return (
      <div className="space-y-6" data-testid="received-efaktura-page">
        <PageHeader title="Prijaté e-faktúry" subtitle="Faktúry doručené vašej firme cez Peppol." />
        <div className="card-surface flex flex-col items-center gap-4 p-12 text-center" data-testid="received-test-mode">
          <div className="grid h-16 w-16 place-items-center rounded-2xl bg-primary/10 text-primary"><Clock className="h-8 w-8" /></div>
          <h3 className="font-heading text-2xl font-bold uppercase">Čoskoro dostupné</h3>
          <p className="max-w-md text-sm text-muted-foreground">Modul Prijaté e-faktúry (Peppol) je zatiaľ v testovacom režime. Čoskoro bude dostupný pre všetky účty. Ďakujeme za trpezlivosť.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="received-efaktura-page">
      <div className="flex items-center justify-between gap-3">
        <PageHeader title="Prijaté e-faktúry" subtitle="Faktúry doručené vašej firme cez Peppol. Potvrďte ich prevzatie." />
        <Button variant="outline" onClick={load} disabled={loading} className="min-h-[44px] gap-2" data-testid="received-refresh"><RefreshCw className="h-4 w-4" /> Obnoviť</Button>
      </div>

      {err && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-500" data-testid="received-error">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {err}
        </div>
      )}

      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : docs.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-12 text-center text-sm text-muted-foreground" data-testid="received-empty">
            <Inbox className="h-8 w-8 opacity-40" /> Zatiaľ žiadne prijaté e-faktúry.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="received-table">
              <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                <tr><th className="px-4 py-2">Prijaté</th><th className="px-4 py-2">Odosielateľ</th><th className="px-4 py-2">Dokument</th><th className="px-4 py-2">Stav</th><th className="px-4 py-2 text-right">Akcia</th></tr>
              </thead>
              <tbody>
                {docs.map((d) => {
                  const id = d.id || d.message_id;
                  return (
                    <tr key={id} className="border-t border-border" data-testid={`received-row-${id}`}>
                      <td className="px-4 py-2 whitespace-nowrap">{(d.received_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-4 py-2 font-mono text-xs">{d.sender || "—"}</td>
                      <td className="px-4 py-2">{d.message_id || id}</td>
                      <td className="px-4 py-2">
                        {d.acknowledged
                          ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> Potvrdené</span>
                          : <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-semibold text-sky-600">Nové</span>}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="ghost" className="gap-1" onClick={() => openDetail(d)} data-testid={`received-view-${id}`}><FileCode className="h-4 w-4" /> Zobraziť</Button>
                          {!d.acknowledged && <Button size="sm" onClick={() => acknowledge(d)} disabled={acking === id} data-testid={`received-ack-${id}`}>{acking === id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Potvrdiť prevzatie"}</Button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl" data-testid="received-detail-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 pr-6">
              <DialogTitle>Prijatá e-faktúra</DialogTitle>
              {!detailLoading && detail?.payload && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" className="gap-1" disabled={downloading === "pdf"} data-testid="received-detail-download">
                      {downloading === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Stiahnuť
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={downloadPdf} data-testid="received-download-pdf"><FileText className="mr-2 h-4 w-4" /> PDF (náhľad)</DropdownMenuItem>
                    <DropdownMenuItem onClick={downloadXml} data-testid="received-download-xml"><FileCode className="mr-2 h-4 w-4" /> XML (UBL)</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (() => {
            const p = parseUbl(detail?.payload);
            if (!p) {
              return <pre className="max-h-[55vh] overflow-auto rounded-lg border border-border bg-muted/40 p-3 text-xs" data-testid="received-detail-payload">{detail?.payload || "Obsah nie je dostupný."}</pre>;
            }
            return (
              <div className="space-y-4 text-sm" data-testid="received-detail-parsed">
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  <div><div className="text-xs uppercase text-muted-foreground">Číslo faktúry</div><div className="font-semibold">{p.number || "—"}</div></div>
                  <div><div className="text-xs uppercase text-muted-foreground">Odosielateľ</div><div className="font-mono text-xs">{detail?.sender || "—"}</div></div>
                  <div><div className="text-xs uppercase text-muted-foreground">Vystavená</div><div>{p.issue || "—"}</div></div>
                  <div><div className="text-xs uppercase text-muted-foreground">Splatnosť</div><div>{p.due || "—"}</div></div>
                  <div><div className="text-xs uppercase text-muted-foreground">Dodávateľ</div><div className="font-medium">{p.supplier || "—"}</div></div>
                  <div><div className="text-xs uppercase text-muted-foreground">Odberateľ</div><div className="font-medium">{p.customer || "—"}</div></div>
                </div>
                {p.lines.length > 0 && (
                  <div className="overflow-hidden rounded-lg border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground"><tr><th className="px-3 py-1.5">Položka</th><th className="px-3 py-1.5 text-right">Množstvo</th><th className="px-3 py-1.5 text-right">Suma</th></tr></thead>
                      <tbody>{p.lines.map((l, i) => (<tr key={i} className="border-t border-border"><td className="px-3 py-1.5">{l.name}</td><td className="px-3 py-1.5 text-right">{l.qty}</td><td className="px-3 py-1.5 text-right">{l.amount} {p.currency}</td></tr>))}</tbody>
                    </table>
                  </div>
                )}
                <div className="flex items-center justify-between rounded-lg bg-primary/10 px-4 py-2">
                  <span className="text-xs uppercase text-muted-foreground">Na úhradu (vrátane DPH)</span>
                  <span className="font-heading text-xl font-bold text-primary" data-testid="received-detail-total">{p.payable || "—"} {p.currency}</span>
                </div>
                <button type="button" className="text-xs text-muted-foreground underline underline-offset-2" onClick={() => setShowRaw((v) => !v)} data-testid="received-detail-toggle-xml">{showRaw ? "Skryť XML" : "Zobraziť XML (UBL)"}</button>
                {showRaw && <pre className="max-h-[40vh] overflow-auto rounded-lg border border-border bg-muted/40 p-3 text-xs" data-testid="received-detail-payload">{detail?.payload}</pre>}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
